import * as braze from "@braze/web-sdk"
import { deferUntilPermitted, BRAZE_DIDOMI_VENDOR_ID, DIDOMI_ANALYTICS_PURPOSE_ID } from "../Didomi"
import { getTotalArticles } from "@theconversation/shared-libraries/article-read-tracking"
import { sendPendingEvents } from "./customEvents"
import { emailToHash } from "../email-hash"
import { brazeTrackingCookiePresent } from "./trackingCookie"
import { nonProdLogger } from "../nonProdLogger"
export { trackCustomEvent } from "./customEvents"
export { trackArticleViewed } from "./articleViewedEvent"
export { trackNewsletterSubscribed } from "./newsletterSubscribedEvent"
export { brazeTrackingCookiePresent, setBrazeTrackingCookie } from "./trackingCookie"
export { trackNewsletterLandingPageViewed } from "./newsletterLandingPageViewedEvent"

/**
 * Boot the Braze Web SDK.
 *
 * In order to avoid blowing through our limited MAU quota, this should only be called
 * when one or more of the following conditions is true:
 * - User has just read their second article
 * - User has just subscribed to a newsletter
 * - User has just made a donation (or started the process to make a donation)
 * - User has previously been activated for Braze
 *
 * This function will not activate Braze until Didomi indicates the user has consented
 * to tracking, or is in a region where consent is not required.
 *
 * Note that the promise returned by this function may not be resolved quickly, or even
 * ever, if the user does not accept the Didomi consent dialog. It can be used to chain
 * further Braze calls.
 */
export function brazeInitialise(
  sdkEndpoint: string,
  apiKey: string,
  userEmail: string | null = null,
): Promise<void> {
  return new Promise<void>((resolve) => {
    // If Braze is already in session, there's no need to re-initialise it.
    if (braze.isInitialized()) {
      resolve()
      return
    }

    deferUntilPermitted({
      vendorId: BRAZE_DIDOMI_VENDOR_ID,
      purposeId: DIDOMI_ANALYTICS_PURPOSE_ID,
      callback: async () => {
        braze.initialize(apiKey, {
          baseUrl: sdkEndpoint,
          enableLogging: true,
          allowUserSuppliedJavascript: false, // set to `true` to support custom HTML messages
        })

        if (userEmail) {
          const emailHash = await emailToHash(userEmail)
          if (emailHash != null) braze.changeUser(emailHash)
        }

        braze.openSession()

        sendPendingEvents()

        resolve()
      },
    })
  })
}

/**
 * Given a user's email address, identify them within Braze.
 *
 * Requires that brazeInitialise() has been called already.
 *
 * The error catching is there because our browser specs on plain HTTP dont have access to the
 * browser cryptography functions needed to generate an email hash, but also it doesn't
 * matter. And it makes sense to avoid blowing up a Reader's browser too, if they're on
 * something old or unsupported.
 *
 * @returns A promise which when resolves indicates that the user change has been actioned.
 */
export function identifyBrazeUser(email: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    deferUntilPermitted({
      vendorId: BRAZE_DIDOMI_VENDOR_ID,
      purposeId: DIDOMI_ANALYTICS_PURPOSE_ID,
      callback: () => {
        emailToHash(email)
          .then((emailHash) => {
            if (emailHash == null) {
              const message = "Empty or invalid email provided to identifyBrazeUser"
              nonProdLogger(message)
              reject(new Error(message))
            } else {
              braze.changeUser(emailHash)
              resolve()
            }
          })
          .catch((reason) => {
            const message =
              reason instanceof Error
                ? `Could not set external id for Braze user: ${reason.message}`
                : "Could not set external id for Braze user"
            nonProdLogger(message)
            reject(new Error(message))
          })
      },
    })
  })
}

/**
 * This function checks if the business rules have been met such that we should track
 * this user with Braze's analytics. The conditions are meant to be as follows, but
 * these are not currently handled fully or correctly:
 * - User has read at least 2 articles in the last 30 days (currently just implemented as 2 articles for all time)
 * - User is a newsletter subscriber (not yet implemented)
 * - User is a financial donor (how recently?) (not yet implemented)
 *
 */
export async function conditionsMetToTrackUser() {
  if (getTotalArticles() >= 2) {
    return true
  }

  if (await brazeTrackingCookiePresent()) {
    return true
  }

  // Is this too fragile a method to tell the user is on the newsletter landing page?
  if (/^\/?[a-zA-Z-]+\/\bnewsletters\b/.test(window.location.pathname)) {
    return true
  }

  return false
}
